import React, { useState, useEffect } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Home as HomeIcon,
  //Dashboard as DashboardIcon,
  //Store as MarketplaceIcon,
  //Description as ProposalsIcon,
  //Analytics as AnalyticsIcon,
  Settings as SettingsIcon,
  EmojiEvents as LeaderboardIcon,
  Menu,
  MenuOpen,
  Build as BuildIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import AccountMenu from '../AccountMenu.tsx';
import Logo from '../common/Logo.tsx';

const expandedWidth = 280;
const collapsedWidth = 72;

const menuItems = [
  { text: 'Home', icon: <HomeIcon />, path: '/home' },
  { text: 'Problem Workspace', icon: <BuildIcon />, path: '/problem-workspace' },
  //{ text: 'Proposals', icon: <ProposalsIcon />, path: '/proposals' },
  //{ text: 'Marketplace', icon: <MarketplaceIcon />, path: '/marketplace' },
  //{ text: 'Executive', icon: <DashboardIcon />, path: '/executive/ceo' },
  //{ text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
  //{ text: 'Analytics', icon: <AnalyticsIcon />, path: '/analytics' },
  { text: 'Attribution', icon: <LeaderboardIcon />, path: '/attribution' },
];

interface SidebarProps {
  onToggle?: (expanded: boolean) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ onToggle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isExpanded, setIsExpanded] = useState(false);

  // Auto-collapse when on the Problem Workspace page
  useEffect(() => {
    if (location.pathname === '/problem-workspace') {
      setIsExpanded(false);
    }
  }, [location.pathname]);

  // Notify parent component when expanded state changes
  useEffect(() => {
    if (onToggle) {
      onToggle(isExpanded);
    }
  }, [isExpanded, onToggle]);

  const toggleDrawer = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: isExpanded ? expandedWidth : collapsedWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: isExpanded ? expandedWidth : collapsedWidth,
          boxSizing: 'border-box',
          background: 'rgba(26, 26, 46, 0.8)',
          backdropFilter: 'blur(10px)',
          borderRight: '1px solid rgba(138, 43, 226, 0.2)',
          boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
          transition: 'width 0.3s ease',
          overflowX: 'hidden',
        },
      }}
    >
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        {isExpanded && (
          <Logo size="large" />
        )}
      </Box>
      <Box sx={{ p: 2 }}>
        {isExpanded && <AccountMenu />}
      </Box>
      <Divider sx={{ borderColor: 'rgba(138, 43, 226, 0.2)' }} />
      <Box sx={{ overflow: 'auto', flexGrow: 1 }}>
        <List>
          {menuItems.map((item) => (
            <ListItem
              button
              key={item.text}
              selected={location.pathname === item.path}
              onClick={() => navigate(item.path)}
              sx={{
                color: '#fff',
                minHeight: 48,
                px: 2.5,
                '&:hover': {
                  background: 'rgba(138, 43, 226, 0.1)',
                },
                '&.Mui-selected': {
                  background: 'rgba(138, 43, 226, 0.2)',
                  borderRight: '3px solid #8A2BE2',
                  '&:hover': {
                    background: 'rgba(138, 43, 226, 0.25)',
                  },
                },
              }}
            >
              <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>{item.icon}</ListItemIcon>
              {isExpanded && <ListItemText primary={item.text} />}
            </ListItem>
          ))}
        </List>
        <Divider sx={{ borderColor: 'rgba(138, 43, 226, 0.2)', my: 1 }} />
        <List>
          <ListItem
            button
            selected={location.pathname === '/settings'}
            onClick={() => navigate('/settings')}
            sx={{
              color: '#fff',
              minHeight: 48,
              px: 2.5,
              '&:hover': {
                background: 'rgba(138, 43, 226, 0.1)',
              },
              '&.Mui-selected': {
                background: 'rgba(138, 43, 226, 0.2)',
                borderRight: '3px solid #8A2BE2',
                '&:hover': {
                  background: 'rgba(138, 43, 226, 0.25)',
                },
              },
            }}
          >
            <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>
              <SettingsIcon />
            </ListItemIcon>
            {isExpanded && <ListItemText primary="Settings" />}
          </ListItem>
        </List>
      </Box>
      <Box sx={{ 
        p: 1, 
        display: 'flex', 
        justifyContent: 'flex-end',
        borderTop: '1px solid rgba(138, 43, 226, 0.2)',
      }}>
        <Tooltip title={isExpanded ? "Collapse Sidebar" : "Expand Sidebar"}>
          <IconButton
            onClick={toggleDrawer}
            sx={{
              color: 'rgba(138, 43, 226, 0.8)',
              '&:hover': {
                color: 'rgba(0, 255, 255, 0.8)',
                backgroundColor: 'rgba(0, 255, 255, 0.1)',
              },
            }}
          >
            {isExpanded ? <MenuOpen /> : <Menu />}
          </IconButton>
        </Tooltip>
      </Box>
    </Drawer>
  );
};

export default Sidebar; 